import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Row,
  Col,
  Table,
} from "reactstrap";
import axios from "axios";
import { END_POINT } from "../../../constants/defaultValues";
import { NotificationManager } from "../../../components/common/react-notifications";
import { getImageUrl } from "../../../helpers/Utils";

const DEFAULT_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 320 240'%3E%3Crect fill='%23f0f0f0' width='320' height='240'/%3E%3Cpath fill='%23ccc' d='M160 80c-22 0-40 18-40 40s18 40 40 40 40-18 40-40-18-40-40-40zm0 64c-13.3 0-24-10.7-24-24s10.7-24 24-24 24 10.7 24 24-10.7 24-24 24z'/%3E%3Cpath fill='%23ccc' d='M240 180H80l40-50 20 25 40-50 60 75z'/%3E%3C/svg%3E";

const getTemplateImageUrl = (image) => getImageUrl(image, DEFAULT_PLACEHOLDER);

const AddFromTemplateModal = ({ isOpen, toggle, onImportSuccess, businessType }) => {
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState({}); // { [templateId]: { checked, price, costPrice, stockCount, brand_name, category_name } }
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("ALL");
  const [selectedBrandFilter, setSelectedBrandFilter] = useState("ALL");

  // Fetch templates when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen, search]);

  useEffect(() => {
    if (isOpen) {
      setSelectedCategoryFilter("ALL");
      setSelectedBrandFilter("ALL");
    }
  }, [isOpen]);

  // Auto-reset category filter if it becomes invalid under selected brand
  useEffect(() => {
    if (selectedCategoryFilter !== "ALL" && templates.length > 0) {
      const isValid = templates.some(t => 
        t.category_name === selectedCategoryFilter && 
        (selectedBrandFilter === "ALL" || t.brand_name === selectedBrandFilter)
      );
      if (!isValid) {
        setSelectedCategoryFilter("ALL");
      }
    }
  }, [selectedBrandFilter, selectedCategoryFilter, templates]);

  // Auto-reset brand filter if it becomes invalid under selected category
  useEffect(() => {
    if (selectedBrandFilter !== "ALL" && templates.length > 0) {
      const isValid = templates.some(t => 
        t.brand_name === selectedBrandFilter && 
        (selectedCategoryFilter === "ALL" || t.category_name === selectedCategoryFilter)
      );
      if (!isValid) {
        setSelectedBrandFilter("ALL");
      }
    }
  }, [selectedCategoryFilter, selectedBrandFilter, templates]);

  const fetchTemplates = async () => {
    setLoading(true);
    
    let industryType = "TAP_HOA";
    if (businessType) {
      const bt = businessType.trim().toLowerCase();
      if (bt === "grocery") {
        industryType = "TAP_HOA";
      } else if (bt === "spa" || bt === "salon") {
        industryType = "spa";
      } else if (bt === "pharmacy") {
        industryType = "pharmacy";
      } else if (bt === "electronics") {
        industryType = "electronic";
      } else if (["cafe", "restaurant", "retail", "fashion", "cosmetic"].includes(bt)) {
        industryType = bt;
      } else if (bt === "cosmetics") {
        industryType = "cosmetic";
      }
    }

    try {
      const response = await axios.get(`${END_POINT}/product-template`, {
        params: {
          industry_type: industryType,
          search: search,
        },
      });
      if (response.data && response.data.data) {
        setTemplates(response.data.data);
        
        // Initialize selection state for new items
        setSelectedItems(prev => {
          const newState = { ...prev };
          response.data.data.forEach(item => {
            if (!newState[item.id]) {
              const sugPrice = item.suggested_price && parseFloat(item.suggested_price) > 0 
                ? String(Math.round(parseFloat(item.suggested_price))) 
                : "10000";
              const sugCost = item.suggested_cost_price && parseFloat(item.suggested_cost_price) > 0 
                ? String(Math.round(parseFloat(item.suggested_cost_price))) 
                : "7000";
              newState[item.id] = {
                checked: false,
                price: sugPrice,
                costPrice: sugCost,
                stockCount: "10",
                brand_name: item.brand_name || "",
                category_name: item.category_name || "",
              };
            }
          });
          return newState;
        });
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      NotificationManager.error("Không thể tải danh sách sản phẩm mẫu", "Lỗi", 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (id) => {
    setSelectedItems(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        checked: !prev[id].checked
      }
    }));
  };

  const handleInputChange = (id, field, value) => {
    setSelectedItems(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  const handleImport = async () => {
    const itemsToCopy = Object.keys(selectedItems)
      .filter(id => selectedItems[id].checked)
      .map(id => {
        const template = templates.find(t => t.id === parseInt(id));
        return {
          templateId: parseInt(id),
          price: parseFloat(selectedItems[id].price) || 0,
          costPrice: parseFloat(selectedItems[id].costPrice) || 0,
          stockCount: parseFloat(selectedItems[id].stockCount) || 0,
          brand_name: selectedItems[id].brand_name !== undefined ? selectedItems[id].brand_name : (template?.brand_name || ""),
          category_name: selectedItems[id].category_name !== undefined ? selectedItems[id].category_name : (template?.category_name || ""),
        };
      });

    if (itemsToCopy.length === 0) {
      NotificationManager.warning("Vui lòng chọn ít nhất một sản phẩm", "Cảnh báo", 3000);
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${END_POINT}/product-template/copy`, {
        items: itemsToCopy,
      });

      if (response.data && response.data.status === 200) {
        NotificationManager.success(response.data.message, "Thành công", 3000);
        // Clear selections
        setSelectedItems({});
        onImportSuccess();
        toggle();
      } else {
        NotificationManager.error(response.data.error || "Có lỗi xảy ra", "Lỗi", 3000);
      }
    } catch (error) {
      console.error("Error copying templates:", error);
      NotificationManager.error("Nhập hàng mẫu thất bại", "Lỗi", 3000);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = Object.values(selectedItems).filter(item => item.checked).length;

  const availableCategories = ["ALL", ...new Set(
    templates
      .filter(item => selectedBrandFilter === "ALL" || item.brand_name === selectedBrandFilter)
      .map(t => t.category_name)
      .filter(Boolean)
  )];

  const availableBrands = ["ALL", ...new Set(
    templates
      .filter(item => selectedCategoryFilter === "ALL" || item.category_name === selectedCategoryFilter)
      .map(t => t.brand_name)
      .filter(Boolean)
  )];

  const filteredTemplates = templates.filter(item => {
    const matchesCategory = selectedCategoryFilter === "ALL" || item.category_name === selectedCategoryFilter;
    const matchesBrand = selectedBrandFilter === "ALL" || item.brand_name === selectedBrandFilter;
    return matchesCategory && matchesBrand;
  });

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="add-from-template-modal">
      <ModalHeader toggle={toggle}>
        <span className="font-weight-bold">Nhập sản phẩm mẫu (Tạp hóa & FMCG)</span>
      </ModalHeader>
      
      <ModalBody style={{ maxHeight: "65vh", overflowY: "auto" }}>
        <Row className="mb-3">
          <Col md="6" xxs="12" className="mb-2 mb-md-0">
            <Input
              type="text"
              placeholder="Tìm kiếm sản phẩm mẫu nhanh..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedCategoryFilter("ALL");
                setSelectedBrandFilter("ALL");
              }}
            />
          </Col>
          <Col md="3" xxs="6">
            <Input
              type="select"
              value={selectedCategoryFilter}
              onChange={(e) => setSelectedCategoryFilter(e.target.value)}
              style={{ height: "38px" }}
            >
              {availableCategories.map(cat => (
                <option key={cat} value={cat}>{cat === "ALL" ? "Tất cả danh mục" : cat}</option>
              ))}
            </Input>
          </Col>
          <Col md="3" xxs="6">
            <Input
              type="select"
              value={selectedBrandFilter}
              onChange={(e) => setSelectedBrandFilter(e.target.value)}
              style={{ height: "38px" }}
            >
              {availableBrands.map(br => (
                <option key={br} value={br}>{br === "ALL" ? "Tất cả thương hiệu" : br}</option>
              ))}
            </Input>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-5">
            <div className="loading" />
            <p className="text-muted mt-2">Đang tải sản phẩm mẫu...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-5 text-muted">
            Không tìm thấy sản phẩm mẫu nào phù hợp.
          </div>
        ) : (
          <Table responsive hover className="align-middle">
            <thead>
              <tr>
                <th style={{ width: "40px", textAlign: "center", verticalAlign: "middle" }} />
                <th style={{ width: "60px", textAlign: "center", verticalAlign: "middle" }}>Ảnh</th>
                <th style={{ textAlign: "center", verticalAlign: "middle" }}>Tên sản phẩm</th>
                <th style={{ width: "150px", textAlign: "center", verticalAlign: "middle" }}>Thương hiệu</th>
                <th style={{ width: "150px", textAlign: "center", verticalAlign: "middle" }}>Danh mục</th>
                <th style={{ width: "80px", textAlign: "center", verticalAlign: "middle" }}>Đơn vị</th>
                <th style={{ width: "120px", textAlign: "center", verticalAlign: "middle" }}>Giá bán lẻ</th>
                <th style={{ width: "120px", textAlign: "center", verticalAlign: "middle" }}>Giá nhập</th>
                <th style={{ width: "90px", textAlign: "center", verticalAlign: "middle" }}>Tồn kho</th>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.map((item) => {
                const state = selectedItems[item.id] || { 
                  checked: false, 
                  price: "0", 
                  costPrice: "0", 
                  stockCount: "0",
                  brand_name: "",
                  category_name: ""
                };
                return (
                  <tr key={item.id} className={state.checked ? "table-active" : ""}>
                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                      <Input
                        type="checkbox"
                        checked={state.checked}
                        onChange={() => handleCheckboxChange(item.id)}
                        style={{ cursor: "pointer" }}
                      />
                    </td>
                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                      <img
                        src={getTemplateImageUrl(item.image)}
                        alt={item.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = DEFAULT_PLACEHOLDER;
                        }}
                        style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
                      />
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      <div className="font-weight-medium">{item.name}</div>
                      <small className="text-muted">Mã: {item.barcode || "N/A"}</small>
                    </td>
                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                      {item.brand_name ? (
                        item.brand_name
                      ) : (
                        <Input
                          type="text"
                          disabled={!state.checked}
                          value={state.brand_name || ""}
                          onChange={(e) => handleInputChange(item.id, "brand_name", e.target.value)}
                          placeholder="Nhập thương hiệu"
                          size="sm"
                          style={{ fontSize: "0.8rem", textAlign: "center" }}
                        />
                      )}
                    </td>
                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                      {item.category_name ? (
                        <span className="badge badge-light badge-pill font-weight-semibold">
                          {item.category_name}
                        </span>
                      ) : (
                        <Input
                          type="text"
                          disabled={!state.checked}
                          value={state.category_name || ""}
                          onChange={(e) => handleInputChange(item.id, "category_name", e.target.value)}
                          placeholder="Nhập danh mục"
                          size="sm"
                          style={{ fontSize: "0.8rem", textAlign: "center" }}
                        />
                      )}
                    </td>
                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>{item.sale_unit || "Cái"}</td>
                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                      <Input
                        type="number"
                        disabled={!state.checked}
                        value={state.price}
                        onChange={(e) => handleInputChange(item.id, "price", e.target.value)}
                        placeholder="Giá bán"
                        size="sm"
                        style={{ textAlign: "center" }}
                      />
                    </td>
                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                      <Input
                        type="number"
                        disabled={!state.checked}
                        value={state.costPrice}
                        onChange={(e) => handleInputChange(item.id, "costPrice", e.target.value)}
                        placeholder="Giá nhập"
                        size="sm"
                        style={{ textAlign: "center" }}
                      />
                    </td>
                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                      <Input
                        type="number"
                        disabled={!state.checked}
                        value={state.stockCount}
                        onChange={(e) => handleInputChange(item.id, "stockCount", e.target.value)}
                        placeholder="SL"
                        size="sm"
                        style={{ textAlign: "center" }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={submitting}>
          Đóng
        </Button>
        <Button
          color="primary"
          onClick={handleImport}
          disabled={submitting || selectedCount === 0}
        >
          {submitting ? "Đang nhập..." : `Xác nhận nhập (${selectedCount} sản phẩm)`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddFromTemplateModal;
